import { createContext, useState, useContext, useCallback } from 'react';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import ErrorLogger from 'entities/helpers/error-log-helper';
import PropTypes from 'prop-types';
import { useIdentity } from 'base/identity-resolver';
import { createEmailSearchFunction } from '../utils/email-search-utils';

const EmailDraftsContext = createContext();

const LIMIT = 20;

const EmailDraftsProvider = ({ children }) => {
  const [drafts, setDrafts] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [selectedEmailAddresses, setSelectedEmailAddresses] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftTotal, setDraftTotal] = useState(0);
  const [hasEverHadData, setHasEverHadData] = useState(false);
  const [page, setPage] = useState(1);
  const { user = {} } = useIdentity();

  const getAllDraftsByUser = async () => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.DRAFTS.get({
        params: {
          offset: (page - 1) * LIMIT,
          limit: LIMIT,
        },
      });
      const { items, total } = res;
      setDrafts(items);
      setDraftTotal(total);
      if (items?.length > 0) {
        setHasEverHadData(true);
      }
    } catch (e) {
      ErrorLogger.error('Error while getting email drafts', { extra: { e, page_name: 'Outreach Email Drafts' } });
    } finally {
      setLoading(false);
    }
  };

  const createDraft = async (payload, drawerId, { disableAlerts = false } = {}) => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.DRAFTS.post({
        data: JSON.stringify(payload),
        disableAlerts,
      });
      if (!disableAlerts) EverTrue.Alert.success(`Successfully created draft.`);

      const isDateRangeValid = dateRange?.from !== null && dateRange?.to !== null;
      const isFilterApplied = isDateRangeValid || selectedEmailAddresses?.length > 0 || selectedNames?.length > 0;

      if (isFilterApplied) {
        await searchDrafts({ dateRange, emailRecipients: selectedEmailAddresses, selectedNames });
      } else {
        setDrafts([res, ...drafts]);
        setDraftTotal((prev) => prev + 1);
      }
      return res;
    } catch (e) {
      ErrorLogger.error('Error while creating email draft', { extra: { e, page_name: 'Outreach Email Drafts' } });
      throw e;
    } finally {
      removeDraftFromSessionStorage(drawerId);
      setLoading(false);
    }
  };

  const updateDraft = async (payload, id, drawerId) => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.DRAFT.put({
        urlArgs: { id },
        data: JSON.stringify(payload),
      });
      EverTrue.Alert.success(`Successfully updated draft.`);
      setDrafts(drafts.map((draft) => (draft.id === id ? res : draft)));
    } catch (e) {
      ErrorLogger.error('Error while updating email draft', { extra: { e, page_name: 'Outreach Email Drafts' } });
    } finally {
      removeDraftFromSessionStorage(drawerId);
      setLoading(false);
    }
  };

  const bulkDeleteDrafts = async (selectedDraftIds) => {
    try {
      await Api.OUTREACH.BULK_DELETE_DRAFTS.delete({
        data: JSON.stringify(selectedDraftIds),
      });
      EverTrue.Alert.success(`Successfully deleted drafts.`);
      setDraftTotal((prev) => Math.max(0, prev - selectedDraftIds.length));
      await getAllDraftsByUser();
    } catch (e) {
      ErrorLogger.error('Error while deleting email drafts', { extra: { error: e, page_name: 'Bulk Delete Drafts' } });
    }
  };

  const deleteDraftById = async (draftId, drawerId, hideAlert = false) => {
    setLoading(true);
    try {
      await Api.OUTREACH.DRAFT.delete({
        urlArgs: { id: draftId },
      });
      !hideAlert && EverTrue.Alert.success(`Successfully deleted draft.`);
      setDrafts(drafts.filter((draft) => draft.id !== draftId));
      setDraftTotal((prev) => Math.max(0, prev - 1));
    } catch (e) {
      ErrorLogger.error('Error while deleting email draft', { extra: { error: e, page_name: 'Delete Draft' } });
    } finally {
      removeDraftFromSessionStorage(drawerId);
      setLoading(false);
    }
  };

  const saveDraftToSessionStorage = (values, drawerId) => {
    try {
      sessionStorage.setItem(drawerId, JSON.stringify(values));
    } catch (error) {
      console.error('Failed to save draft.');
    }
  };

  const removeDraftFromSessionStorage = (drawerId) => {
    sessionStorage.removeItem(drawerId);
  };

  const onPageChange = (page) => {
    setPage(page);
  };

  const searchDrafts = useCallback(
    async (filters) => {
      return createEmailSearchFunction('draft', user, setDrafts, setDraftTotal, page, LIMIT, setLoading)(filters);
    },
    [page, user, setDrafts, setDraftTotal, setLoading]
  );

  const updateFilters = useCallback(({ dateRange, selectedEmailAddresses, selectedNames }) => {
    if (typeof dateRange !== 'undefined') setDateRange(dateRange);
    if (typeof selectedEmailAddresses !== 'undefined') setSelectedEmailAddresses(selectedEmailAddresses);
    if (typeof selectedNames !== 'undefined') setSelectedNames(selectedNames);
  }, []);

  return (
    <EmailDraftsContext.Provider
      value={{
        drafts,
        loading,
        draftTotal,
        page,
        onPageChange,
        draftLimit: LIMIT,
        getAllDraftsByUser,
        createDraft,
        updateDraft,
        bulkDeleteDrafts,
        deleteDraftById,
        saveDraftToSessionStorage,
        removeDraftFromSessionStorage,
        searchDrafts,
        hasEverHadData,
        updateFilters,
      }}
    >
      {children}
    </EmailDraftsContext.Provider>
  );
};

const useEmailDrafts = () => {
  const context = useContext(EmailDraftsContext);
  if (!context) {
    throw new Error('useEmailDrafts must be used within an EmailDraftsProvider');
  }
  return context;
};

EmailDraftsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { EmailDraftsProvider, useEmailDrafts };
